// autoSaveQueue.ts
type SaveFn = (ids: string[], opts?: { keepalive?: boolean }) => Promise<void>;

interface BatchSaveQueueOptions {
  saveFn: SaveFn; // Your API call
  storageKey: string; // REQUIRED: localStorage key
  batchSize: number; // REQUIRED: e.g., 20
  maxWaitMs: number; // REQUIRED: e.g., 2000
  maxRetries?: number; // default 5
  onLog?: (msg: string) => void; // optional logging hook
}

export default class BatchSaveQueue {
  private queue = new Set<string>();
  private timer: number | null = null;
  private flushing = false;
  private retryAttempt = 0;

  constructor(private opts: BatchSaveQueueOptions) {
    if (!opts.storageKey) throw new Error("storageKey is required");
    if (!opts.batchSize || opts.batchSize <= 0) throw new Error("batchSize must be > 0");
    if (!opts.maxWaitMs || opts.maxWaitMs < 0) throw new Error("maxWaitMs must be >= 0");
    this.opts.maxRetries ??= 5;

    // Restore pending items
    const saved = localStorage.getItem(this.opts.storageKey);
    if (saved) {
      try {
        const arr: unknown = JSON.parse(saved);
        if (Array.isArray(arr)) {
          arr.forEach((id: unknown) => {
            if (typeof id === "string") this.queue.add(id);
          });
          this.log(`restored ${this.queue.size} ids from storage`);
        }
      } catch {
        this.log("failed to parse persisted queue; ignoring");
      }
    }

    // Lifecycle hooks (no beacon): best-effort flush before tab hides; not guaranteed to finish
    window.addEventListener("online", () => {
      this.log("online: scheduling immediate flush");
      this.scheduleFlush(0);
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        // this.log("hidden: attempting flush (keepalive)");
        // Try to finish the request during page transition
        void this.flush(true);
      }
    });

    // pagehide/beforeunload won't guarantee completion, but we can still attempt with keepalive
    window.addEventListener("pagehide", () => {
      // this.log("pagehide: attempting flush (keepalive)");
      void this.flush(true);
    });
    window.addEventListener("beforeunload", () => {
      // this.log("beforeunload: attempting flush (keepalive)");
      void this.flush(true);
    });
  }

  enqueue(id: string) {
    if (!id) return;
    const before = this.queue.size;
    this.queue.add(id);
    if (this.queue.size !== before) this.log(`enqueue ${id} (queue=${this.queue.size})`);
    this.persist();

    // Flush immediately if we hit the batch size, otherwise within maxWaitMs
    if (this.queue.size >= this.opts.batchSize) {
      this.scheduleFlush(0);
    } else {
      this.scheduleFlush(this.opts.maxWaitMs);
    }
  }

  // ---- internals ----

  private scheduleFlush(delayMs: number) {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.flush(), delayMs) as unknown as number;
    this.log(`scheduled flush in ${delayMs}ms`);
  }

  private async flush(keepalive = false) {
    if (this.flushing) {
      this.log("flush skipped: already flushing");
      return;
    }
    if (!navigator.onLine) {
      this.log("flush skipped: offline");
      return;
    }

    const items = Array.from(this.queue).slice(0, this.opts.batchSize);
    if (items.length === 0) {
      // this.log("flush: no items");
      return;
    }

    this.flushing = true;
    this.log(`flush start: sending ${items.length} ids (keepalive=${keepalive})`);

    try {
      await this.opts.saveFn(items, { keepalive });
      items.forEach(id => this.queue.delete(id));
      this.persist();
      this.retryAttempt = 0;

      this.log(`flush success: removed ${items.length}, remaining=${this.queue.size}`);

      // If more remain, chain another quick flush
      if (this.queue.size > 0) this.scheduleFlush(100);
    } catch (err) {
      this.retryAttempt++;
      const attempt = this.retryAttempt;
      const max = this.opts.maxRetries!;
      const base = Math.min(30_000, 500 * 2 ** (attempt - 1));
      const jitter = Math.floor(Math.random() * 250);
      const delay = base + jitter;

      this.log(`flush error (attempt ${attempt}/${max}): ${String(err)}; retrying in ${delay}ms`);

      if (attempt <= max) {
        this.scheduleFlush(delay);
      } else {
        // Give up for now; next enqueue/online/visibility event will try again
        this.log("max retries reached; will wait for next trigger");
        this.scheduleFlush(5_000);
      }
    } finally {
      this.flushing = false;
    }
  }

  private persist() {
    try {
      localStorage.setItem(this.opts.storageKey, JSON.stringify(Array.from(this.queue)));
      this.log(`persisted queue (${this.queue.size})`);
    } catch {
      this.log("persist failed (localStorage quota?)");
    }
  }

  private log(msg: string) {
    try {
      this.opts.onLog?.(msg);
    } catch {
      /* avoid throwing from user logger */
    }
  }
}
